import { useCallback, useState } from 'react'

import { AbortError, isAbortError } from '@app/errors/AbortError'

import { useAppDispatch } from '@app/utils/redux'

import { deleteAuthPasskeyById, getAuthPasskeys, postAuthPasskeyBegin, postAuthPasskeyComplete } from '@app/packages/api/v3/actions/auth'
import { intoResultAsync, unwrapResult } from '@app/packages/Result/Result'

import { callApi } from '@app/store/actions/api'
import { loginAndStore } from '@app/store/actions/session'

/** Checks if WebAuthn is supported in the current environment */
export function isWebAuthnAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.PublicKeyCredential !== 'undefined' && typeof navigator.credentials !== 'undefined'
}

/** Checks if conditional mediation (passkey autofill) is supported */
export async function isConditionalMediationAvailable(): Promise<boolean> {
  if (!isWebAuthnAvailable()) return false
  if (typeof PublicKeyCredential.isConditionalMediationAvailable !== 'function') return false
  return PublicKeyCredential.isConditionalMediationAvailable()
}

/**
 * Hook for registering a new passkey (after SMS login).
 * Returns registerPasskey function that throws on failure.
 */
export function usePasskeyRegistration() {
  const dispatch = useAppDispatch()

  return useCallback(
    async (label?: string) => {
      const challenge = await dispatch(callApi(postAuthPasskeyBegin({ flow: 'enroll', label })))
      const { session_id, public_key } = challenge.data.attributes

      const credentialResult = await intoResultAsync(navigator.credentials.create({ publicKey: creationOptionsFromServer(public_key) }))
      if (credentialResult.error && isDeniedError(credentialResult.value)) throw new AbortError('Authentication cancelled')
      const credential = unwrapResult(credentialResult)
      if (!credential) throw new AbortError('Credential creation cancelled')

      await dispatch(callApi(postAuthPasskeyComplete({ session_id, credential: credentialToJSON(credential as PublicKeyCredential), label })))
    },
    [dispatch]
  )
}

/**
 * Hook for logging in with a passkey (without SMS, discoverable credentials).
 * Returns loginWithPasskey function that throws on failure; on success the v3
 * session is established and the profile is loaded.
 */
export function usePasskeyLogin() {
  const dispatch = useAppDispatch()

  return useCallback(async () => {
    const challenge = await dispatch(callApi(postAuthPasskeyBegin({ flow: 'login' })))
    const { session_id, public_key } = challenge.data.attributes

    const credentialResult = await intoResultAsync(navigator.credentials.get({ publicKey: requestOptionsFromServer(public_key) }))
    if (credentialResult.error && isDeniedError(credentialResult.value)) throw new AbortError('Authentication cancelled')
    const credential = unwrapResult(credentialResult)
    if (!credential) throw new AbortError('Authentication cancelled')

    await dispatch(loginAndStore({ method: 'passkey', session_id, credential: credentialToJSON(credential as PublicKeyCredential) }))
  }, [dispatch])
}

export type PasskeyCredential = {
  id: string
  label: string | null
  created_at: string
  last_used_at: string | null
}

/**
 * Hook for managing user passkeys (list and delete).
 * loadCredentials and removeCredential throw on failure.
 */
export function usePasskeyManagement() {
  const dispatch = useAppDispatch()
  const [credentials, setCredentials] = useState<PasskeyCredential[]>([])

  const loadCredentials = useCallback(async () => {
    const result = await dispatch(callApi(getAuthPasskeys()))
    setCredentials(
      result.data.map(p => ({ id: p.id, label: p.attributes.label, created_at: p.attributes.created_at, last_used_at: p.attributes.last_used_at }))
    )
  }, [dispatch])

  const removeCredential = useCallback(
    async (id: string) => {
      await dispatch(callApi(deleteAuthPasskeyById(id)))
      setCredentials(prev => prev.filter(c => c.id !== id))
    },
    [dispatch]
  )

  return { credentials, loadCredentials, removeCredential }
}

// The server returns W3C PublicKeyCredential*Options verbatim, with binary
// fields (challenge, credential ids) base64url-encoded — decode them into
// ArrayBuffers for the browser API.
function requestOptionsFromServer(options: Record<string, unknown>): PublicKeyCredentialRequestOptions {
  return {
    challenge: base64urlToBuffer(options.challenge as string),
    rpId: options.rpId as string | undefined,
    userVerification: options.userVerification as UserVerificationRequirement | undefined,
    timeout: options.timeout as number | undefined,
    allowCredentials: descriptorsFromServer(options.allowCredentials),
  }
}

function creationOptionsFromServer(options: Record<string, unknown>): PublicKeyCredentialCreationOptions {
  const user = options.user as Record<string, unknown>
  return {
    challenge: base64urlToBuffer(options.challenge as string),
    rp: options.rp as PublicKeyCredentialRpEntity,
    user: { ...user, id: base64urlToBuffer(user.id as string) } as PublicKeyCredentialUserEntity,
    pubKeyCredParams: options.pubKeyCredParams as PublicKeyCredentialParameters[],
    authenticatorSelection: options.authenticatorSelection as AuthenticatorSelectionCriteria | undefined,
    timeout: options.timeout as number | undefined,
    excludeCredentials: descriptorsFromServer(options.excludeCredentials),
  }
}

function descriptorsFromServer(value: unknown): PublicKeyCredentialDescriptor[] | undefined {
  if (!Array.isArray(value)) return undefined
  return (value as Array<{ id: string; transports?: AuthenticatorTransport[] }>).map(c => ({
    type: 'public-key' as const,
    id: base64urlToBuffer(c.id),
    transports: c.transports,
  }))
}

/** v3 expects the W3C camelCase JSON form (unlike the snake_case v2 webauthn API). */
function credentialToJSON(credential: PublicKeyCredential): Record<string, unknown> {
  const response = credential.response

  const result: Record<string, unknown> = {
    id: credential.id,
    rawId: bufferToBase64url(credential.rawId),
    type: credential.type,
  }

  if ('attestationObject' in response) {
    const attestationResponse = response as AuthenticatorAttestationResponse
    result.response = {
      attestationObject: bufferToBase64url(attestationResponse.attestationObject),
      clientDataJSON: bufferToBase64url(attestationResponse.clientDataJSON),
    }
  } else if ('signature' in response) {
    const assertionResponse = response as AuthenticatorAssertionResponse
    result.response = {
      authenticatorData: bufferToBase64url(assertionResponse.authenticatorData),
      clientDataJSON: bufferToBase64url(assertionResponse.clientDataJSON),
      signature: bufferToBase64url(assertionResponse.signature),
      userHandle: assertionResponse.userHandle ? bufferToBase64url(assertionResponse.userHandle) : null,
    }
  }

  return result
}

function base64urlToBuffer(base64url: string): ArrayBuffer {
  const base64 = base64url.replace(/-/g, '+').replace(/_/g, '/')
  const padding = '='.repeat((4 - (base64.length % 4)) % 4)
  const binary = atob(base64 + padding)
  const buffer = new ArrayBuffer(binary.length)
  const view = new Uint8Array(buffer)
  for (let i = 0; i < binary.length; i++) {
    view[i] = binary.charCodeAt(i)
  }
  return buffer
}

function bufferToBase64url(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer)
  let str = ''
  for (const byte of bytes) {
    str += String.fromCharCode(byte)
  }
  return btoa(str).replace(/\+/g, '-').replace(/\//g, '_').replace(/=/g, '')
}

const isDeniedError = (error: unknown): boolean => {
  return isAbortError(error) || (error instanceof DOMException && (error.name === 'NotAllowedError' || error.name === 'AbortError'))
}
